import { supabase } from './supabase';
import { CryptoService } from './crypto';
import CryptoJS from 'crypto-js';

export interface BackupMetadata {
  version: string;
  email: string;
  userId: string;
  createdAt: string;
  appName: string;
  serverIdentifier: string;
  keyVersion: number;
}

export interface EncryptionKeyBackup {
  metadata: BackupMetadata;
  encryptedMasterKey: string;
  backupSignature: string;
}

export interface RestoreValidation {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export class BackupService {
  private static readonly BACKUP_VERSION = '1.0.0';
  private static readonly APP_NAME = 'GHPOnotes';
  private static readonly MAX_BACKUP_SIZE = 1024 * 1024 * 1024;
  private static readonly CHUNK_SIZE = 1024 * 1024 * 5;
  private static readonly OPERATION_TIMEOUT = 300000;

  static generateBackupPassword(userPassword: string, email: string): string {
    return CryptoJS.SHA256(userPassword + email + 'backup_salt').toString();
  }

  private static async withTimeout<T>(
    promise: Promise<T>,
    timeoutMs: number = this.OPERATION_TIMEOUT,
    errorMessage: string = 'Operation timed out'
  ): Promise<T> {
    let timeoutHandle: number | undefined;

    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutHandle = setTimeout(() => {
        reject(new Error(errorMessage));
      }, timeoutMs) as unknown as number;
    });

    try {
      const result = await Promise.race([promise, timeoutPromise]);
      if (timeoutHandle) clearTimeout(timeoutHandle);
      return result;
    } catch (error) {
      if (timeoutHandle) clearTimeout(timeoutHandle);
      throw error;
    }
  }

  private static async readFileInChunks(
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<string> {
    const readPromise = new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      let offset = 0;
      let fileContent = '';
      const totalSize = file.size;
      let lastProgressUpdate = Date.now();

      const readNextChunk = () => {
        if (offset >= totalSize) {
          resolve(fileContent);
          return;
        }

        const chunk = file.slice(offset, offset + this.CHUNK_SIZE);
        reader.readAsText(chunk);
      };

      reader.onload = (e) => {
        if (e.target?.result) {
          fileContent += e.target.result as string;
          offset += this.CHUNK_SIZE;

          const now = Date.now();
          if (onProgress && (now - lastProgressUpdate > 100 || offset >= totalSize)) {
            const progress = Math.min((offset / totalSize) * 100, 100);
            onProgress(progress);
            lastProgressUpdate = now;
          }

          setTimeout(readNextChunk, 0);
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };

      readNextChunk();
    });

    return this.withTimeout(
      readPromise,
      this.OPERATION_TIMEOUT,
      'File reading timed out. The file may be too large or corrupted.'
    );
  }

  static async createBackup(password: string): Promise<Blob | null> {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('User not authenticated');

      const { data: keyData, error: keyError } = await supabase
        .from('user_encryption_keys')
        .select('encrypted_master_key, created_at, updated_at')
        .eq('user_id', user.user.id)
        .maybeSingle();

      if (keyError || !keyData) {
        throw new Error('Master key not found');
      }

      const masterKey = CryptoService.decryptMasterKey(
        keyData.encrypted_master_key,
        password,
        user.user.email!
      );

      if (!masterKey) {
        throw new Error('Invalid password');
      }

      const serverIdentifier = await this.getServerIdentifier();

      const metadata: BackupMetadata = {
        version: this.BACKUP_VERSION,
        email: user.user.email!,
        userId: user.user.id,
        createdAt: new Date().toISOString(),
        appName: this.APP_NAME,
        serverIdentifier,
        keyVersion: 1,
      };

      const backupPassword = this.generateBackupPassword(password, user.user.email!);

      const backupData: EncryptionKeyBackup = {
        metadata,
        encryptedMasterKey: keyData.encrypted_master_key,
        backupSignature: this.generateSignature(
          keyData.encrypted_master_key,
          metadata,
          backupPassword
        ),
      };

      const backupJson = JSON.stringify(backupData, null, 2);

      const encryptedBackup = CryptoJS.AES.encrypt(backupJson, backupPassword).toString();

      const finalBackup = {
        encrypted: encryptedBackup,
        metadata: {
          version: this.BACKUP_VERSION,
          createdAt: metadata.createdAt,
          email: metadata.email,
        },
      };

      const backupBlob = new Blob([JSON.stringify(finalBackup, null, 2)], {
        type: 'application/json',
      });

      if (backupBlob.size > this.MAX_BACKUP_SIZE) {
        throw new Error('Backup size exceeds maximum limit');
      }

      return backupBlob;
    } catch (error) {
      console.error('Backup creation error:', error);
      return null;
    }
  }

  static async restoreBackup(
    backupFile: File,
    password: string,
    email: string,
    onProgress?: (progress: number) => void
  ): Promise<RestoreValidation> {
    const validation: RestoreValidation = {
      isValid: false,
      errors: [],
      warnings: [],
    };

    try {
      if (backupFile.size > this.MAX_BACKUP_SIZE) {
        validation.errors.push('Backup file exceeds maximum size limit');
        return validation;
      }

      if (onProgress) onProgress(5);

      const fileContent = await this.readFileInChunks(backupFile, (readProgress) => {
        if (onProgress) {
          onProgress(5 + (readProgress * 0.15));
        }
      });

      if (onProgress) onProgress(20);

      let parsedBackup: any;

      try {
        parsedBackup = JSON.parse(fileContent);
      } catch (e) {
        validation.errors.push('Invalid backup file format. The file is not valid JSON or may be corrupted.');
        return validation;
      }

      if (!parsedBackup.encrypted || !parsedBackup.metadata) {
        validation.errors.push('Backup file is missing required data. This may not be a valid GHPOnotes backup file.');
        return validation;
      }

      if (parsedBackup.metadata.email !== email) {
        validation.errors.push(
          `Backup is for ${parsedBackup.metadata.email} but you are logged in as ${email}`
        );
        return validation;
      }

      const backupPassword = this.generateBackupPassword(password, email);

      let decryptedBackup: string;
      try {
        const bytes = CryptoJS.AES.decrypt(parsedBackup.encrypted, backupPassword);
        decryptedBackup = bytes.toString(CryptoJS.enc.Utf8);
        if (!decryptedBackup) {
          validation.errors.push('Invalid password or corrupted backup');
          return validation;
        }
      } catch (e) {
        validation.errors.push('Failed to decrypt backup file');
        return validation;
      }

      let backupData: EncryptionKeyBackup;
      try {
        backupData = JSON.parse(decryptedBackup);
      } catch (e) {
        validation.errors.push('Corrupted backup data');
        return validation;
      }

      if (!this.validateBackupStructure(backupData)) {
        validation.errors.push('Invalid backup structure');
        return validation;
      }

      const signatureValid = this.verifySignature(
        backupData.encryptedMasterKey,
        backupData.metadata,
        backupData.backupSignature,
        backupPassword
      );

      if (!signatureValid) {
        validation.errors.push('Backup signature verification failed');
        return validation;
      }

      const testMasterKey = CryptoService.decryptMasterKey(
        backupData.encryptedMasterKey,
        password,
        email
      );

      if (!testMasterKey) {
        validation.errors.push('Unable to decrypt master key with provided password');
        return validation;
      }

      if (backupData.metadata.version !== this.BACKUP_VERSION) {
        validation.warnings.push(
          `Backup version (${backupData.metadata.version}) differs from current version (${this.BACKUP_VERSION})`
        );
      }

      validation.isValid = true;
      return validation;
    } catch (error: any) {
      if (error.message && error.message.includes('timed out')) {
        validation.errors.push('Operation timed out. The file may be too large or the system is under heavy load.');
      } else {
        validation.errors.push(error.message || 'Unexpected error during restore validation');
      }
      return validation;
    }
  }

  static async applyBackup(
    backupFile: File,
    password: string,
    email: string,
    onProgress?: (progress: number) => void
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const validation = await this.restoreBackup(backupFile, password, email, (validationProgress) => {
        if (onProgress) {
          onProgress(validationProgress * 0.3);
        }
      });

      if (!validation.isValid) {
        return {
          success: false,
          error: validation.errors.join('; '),
        };
      }

      if (onProgress) onProgress(30);

      const { data: user } = await supabase.auth.getUser();
      if (!user.user) {
        return { success: false, error: 'User not authenticated' };
      }

      if (onProgress) onProgress(35);

      const fileContent = await this.readFileInChunks(backupFile, (readProgress) => {
        if (onProgress) {
          onProgress(35 + (readProgress * 0.25));
        }
      });

      if (onProgress) onProgress(60);

      const parsedBackup = JSON.parse(fileContent);

      const backupPassword = this.generateBackupPassword(password, email);
      const bytes = CryptoJS.AES.decrypt(parsedBackup.encrypted, backupPassword);
      const decryptedBackup = bytes.toString(CryptoJS.enc.Utf8);
      const backupData: EncryptionKeyBackup = JSON.parse(decryptedBackup);

      if (onProgress) onProgress(75);

      const { error: updateError } = await supabase
        .from('user_encryption_keys')
        .update({
          encrypted_master_key: backupData.encryptedMasterKey,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', user.user.id);

      if (onProgress) onProgress(85);

      if (updateError) {
        return {
          success: false,
          error: 'Failed to update encryption key in database',
        };
      }

      if (onProgress) onProgress(90);

      const masterKey = CryptoService.decryptMasterKey(
        backupData.encryptedMasterKey,
        password,
        email
      );

      if (masterKey) {
        CryptoService.setMasterKey(masterKey, email, password);
      }

      if (onProgress) onProgress(100);

      return { success: true };
    } catch (error: any) {
      if (error.message && error.message.includes('timed out')) {
        return {
          success: false,
          error: 'Operation timed out. The file may be too large or corrupted.',
        };
      }
      return {
        success: false,
        error: error.message || 'Unexpected error applying backup',
      };
    }
  }

  private static validateBackupStructure(data: any): boolean {
    return !!(
      data &&
      data.metadata &&
      data.metadata.version &&
      data.metadata.email &&
      data.metadata.userId &&
      data.metadata.createdAt &&
      data.encryptedMasterKey &&
      data.backupSignature
    );
  }

  private static generateSignature(
    encryptedMasterKey: string,
    metadata: BackupMetadata,
    backupPassword: string
  ): string {
    const dataToSign = JSON.stringify({
      encryptedMasterKey,
      metadata,
    });
    return CryptoJS.HmacSHA256(dataToSign, backupPassword).toString();
  }

  private static verifySignature(
    encryptedMasterKey: string,
    metadata: BackupMetadata,
    signature: string,
    backupPassword: string
  ): boolean {
    const expectedSignature = this.generateSignature(
      encryptedMasterKey,
      metadata,
      backupPassword
    );
    return signature === expectedSignature;
  }

  private static async getServerIdentifier(): Promise<string> {
    try {
      const { data } = await supabase.auth.getSession();
      return CryptoJS.SHA256(
        data.session?.user?.id || 'unknown' + new Date().getTime()
      ).toString().substring(0, 16);
    } catch (e) {
      return 'unknown';
    }
  }

  static downloadBackup(blob: Blob, email: string) {
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `ghponotes-backup-${email.split('@')[0]}-${timestamp}.ghpbackup`;

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}
